import * as bech32 from 'bech32';
import * as HEX from 'crypto-js/enc-hex';
import RIPEMD160 from 'crypto-js/ripemd160';
import SHA256 from 'crypto-js/sha256';
import { StdSignature } from '../core';
import { StdTx } from '../core';
import { StdSignMsg } from '../core';
import { AccAddress, AccPubKey, ValAddress, ValPubKey } from '../core';

export const BECH32_PUBKEY_DATA_PREFIX = 'eb5ae98721';

function addressFromPublicKey(publicKey: Buffer): Buffer {
  if (typeof publicKey !== 'object' || !(publicKey instanceof Buffer)) {
    throw new TypeError('parameter must be Buffer that contains public key');
  }
  const message = HEX.parse(publicKey.toString('hex'));
  const hash = RIPEMD160(SHA256(message) as any).toString();
  const address = Buffer.from(hash, 'hex');
  return Buffer.from(bech32.toWords(address));
}

function pubKeyFromPublicKey(publicKey: Buffer): Buffer {
  const buffer = Buffer.from(BECH32_PUBKEY_DATA_PREFIX, 'hex');
  const combined = Buffer.concat([buffer, publicKey]);
  return Buffer.from(bech32.toWords(combined));
}

export function prepareSignBytes(jsonTx: any): any {
  if (Array.isArray(jsonTx)) {
    return jsonTx.map(prepareSignBytes);
  }

  if (typeof jsonTx !== 'object') {
    return jsonTx;
  }

  const sorted: any = {};
  Object.keys(jsonTx)
    .sort()
    .forEach(key => {
      if (jsonTx[key] === undefined || jsonTx[key] === null) return;
      sorted[key] = prepareSignBytes(jsonTx[key]);
    });
}

export abstract class Key {
  public abstract sign(payload: Buffer): Buffer;
  public accAddress: AccAddress;
  public valAddress: ValAddress;
  public accPubKey: AccPubKey;
  public valPubKey: ValPubKey;

  constructor(public publicKey: Buffer) {
    const rawAddress = addressFromPublicKey(publicKey);
    const rawPubKey = pubKeyFromPublicKey(publicKey);
    this.accAddress = bech32.encode('terra', Array.from(rawAddress));
    this.valAddress = bech32.encode('terravaloper', Array.from(rawAddress));
    this.accPubKey = bech32.encode('terrapub', Array.from(rawPubKey));
    this.valPubKey = bech32.encode('terravaloperpub', Array.from(rawPubKey));
  }

  public createSignature(tx: StdSignMsg): StdSignature {
    const sigData = this.sign(Buffer.from(tx.toJSON()));
    return StdSignature.fromData({
      signature: sigData.toString('base64'),
      pub_key: {
        type: 'tendermint/PubKeySecp256k1',
        value: this.publicKey.toString('base64'),
      },
    });
  }

  public signTx(tx: StdSignMsg): StdTx {
    const sig = this.createSignature(tx);
    return new StdTx(tx.msgs, tx.fee, [sig], tx.memo);
  }
}
